import { PreferenceCenter } from "@ua/web-sdk/lib/remote-data/preference-center"
import * as React from "react"
import { EmailPreferenceCenterEntry } from "../bootstrap"
import Feedback from "../Feedback"
import { EmailPreferenceCenterTranslation } from "../i18n"
import EmailPreferenceCenter from "./shared/EmailPreferenceCenter"
import useChannelSubscriptionManager from "./hooks/useChannelSubscriptionManager"
import {
  EmailOptOut,
  SavePreferences,
  Subscriptions,
  UpdateSubscriptions,
} from "./hooks/types"

type WrapperProps = {
  channelId: string
  preferenceCenter: PreferenceCenter
  translation: EmailPreferenceCenterTranslation
  entry: EmailPreferenceCenterEntry
  unsubscribe: EmailOptOut
}

type Props = {
  preferenceCenter: PreferenceCenter
  translation: EmailPreferenceCenterTranslation
  subscriptions: Subscriptions
  updateSubscriptions: UpdateSubscriptions
  unsubscribe: EmailOptOut
  savePreferences: SavePreferences
}

type SaveStatus = "idle" | "saved" | "error"

const ChannelPreferenceCenterWrapper: React.FC<WrapperProps> = ({
  channelId,
  preferenceCenter,
  translation,
  entry,
  unsubscribe,
}) => {
  const [
    loading,
    subscriptions,
    updateSubscriptions,
    savePreferences,
  ] = useChannelSubscriptionManager(preferenceCenter, channelId, entry)
  if (loading) return <>{translation.loading}</>
  return (
    <ChannelPreferenceCenter
      preferenceCenter={preferenceCenter}
      translation={translation}
      subscriptions={subscriptions}
      updateSubscriptions={updateSubscriptions}
      unsubscribe={unsubscribe}
      savePreferences={savePreferences}
    />
  )
}

const ChannelPreferenceCenter: React.FC<Props> = ({
  preferenceCenter,
  translation,
  subscriptions,
  updateSubscriptions,
  unsubscribe,
  savePreferences,
}) => {
  const [saveStatus, updateSaveStatus] = React.useState<SaveStatus>("idle")
  const onSave = React.useCallback(async () => {
    try {
      await savePreferences()
      updateSaveStatus("saved")
    } catch (e) {
      console.error(e)
      updateSaveStatus("error")
    }
  }, [savePreferences])
  return (
    <>
      <EmailPreferenceCenter
        preferenceCenter={preferenceCenter}
        subscriptionLists={subscriptions}
        updateSubscriptions={updateSubscriptions}
        translation={translation}
        onSave={onSave}
        onUnsubscribe={unsubscribe}
      />
      {saveStatus === "error" && (
        <Feedback>{translation.errorSavingPreferences}</Feedback>
      )}
      {saveStatus === "saved" && (
        <Feedback type="success">
          {translation.successSavingPreferences}
        </Feedback>
      )}
    </>
  )
}

export default ChannelPreferenceCenterWrapper
